/**
 * Format price using WooCommerce settings
 *
 * @param {number} amount Price amount
 * @returns {string} Formatted price
 */
export function formatPrice(amount) {
	// Use WooCommerce currency settings from Interactivity API state
	const { store } = window.wp?.interactivity || {};
	if (store) {
		const { state } = store('caddy/cart');
		if (state?.currencySymbol) {
			const num = parseFloat(amount) || 0;
			const decimals = state.currencyDecimals ?? 2;
			const decSep = state.currencyDecimalSep || '.';
			const thousSep = state.currencyThousandSep || ',';
			const pos = state.currencyPosition || 'left';

			const parts = num.toFixed(decimals).split('.');
			parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousSep);
			const formatted = parts.join(decSep);
			const sym = state.currencySymbol;

			switch (pos) {
				case 'left': return sym + formatted;
				case 'right': return formatted + sym;
				case 'left_space': return sym + ' ' + formatted;
				case 'right_space': return formatted + ' ' + sym;
				default: return sym + formatted;
			}
		}
	}
	// Fallback
	return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

/**
 * Format a price number using WooCommerce locale settings (no currency symbol).
 * Used for display values where the template adds the symbol separately.
 *
 * @param {number} amount Price amount
 * @returns {string} Formatted number (e.g., "1,200.50" or "1.200,50")
 */
export function formatPriceSmart(amount) {
	const num = parseFloat(amount) || 0;
	// Try to use WooCommerce currency settings from Interactivity API state
	const { store } = window.wp?.interactivity || {};
	if (store) {
		try {
			const { state } = store('caddy/cart');
			if (state?.currencyDecimals !== undefined) {
				const decimals = state.currencyDecimals ?? 2;
				const decSep = state.currencyDecimalSep || '.';
				const thousSep = state.currencyThousandSep || ',';
				const parts = num.toFixed(decimals).split('.');
				parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousSep);
				return parts.join(decSep);
			}
		} catch {
			// Fall through to default
		}
	}
	// Fallback: use 2 decimal places with period separator
	return num.toFixed(2);
}

/**
 * Convert Store API price from cents to dollars with precision handling
 *
 * @param {string|number} priceInCents Price in cents from Store API
 * @returns {number} Price in dollars, properly rounded
 */
export function convertStoreApiPrice(priceInCents) {
	if (!priceInCents) return 0;
	// Convert to number, divide by 100, and round to 2 decimal places to avoid floating point errors
	return Math.round(parseFloat(priceInCents)) / 100;
}
